import React, { forwardRef } from "react";
import styled from "styled-components";
import Select from "react-select";
import { tokens } from "../../public/tokens";

const sizes = {
    xs: {
        padding: "0 8px",
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        borderRadius: "6px",
        height: "28px",
    },
    sm: {
        padding: "0 10px",
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        borderRadius: "6px",
        height: "32px",
    },
    md: {
        padding: "0 10px",
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        borderRadius: "7px",
        height: "36px",
    },
    lg: {
        padding: "0 10px",
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        borderRadius: "7px",
        height: "40px",
    },
};

const defaultFocusVisibleState = {
    outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
    boxShadow: "0px 0px 0px 1px rgba(163, 163, 163, 0.50)",
};

const errorState = {
    outline: `1px solid ${tokens.colors.ui.destructive}`,
    boxShadow: "0px 0px 1px 1px rgba(196, 61, 81, 0.20)",
};

/**
 * Custom styles for react-select.
 * The surrounding SelectContainer handles the main input styling.
 */
const customSelectStyles = {
    control: (provided) => ({
        ...provided,
        border: "none",
        boxShadow: "none",
        backgroundColor: "transparent",
        minHeight: "auto",
        height: "100%",
        "&:hover": {
            border: "none",
        },
    }),

    valueContainer: (provided) => ({
        ...provided,
        padding: 0,
        paddingRight: "70px",
        height: "100%",
    }),

    input: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
        color: tokens.colors.ui.foreground,
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: tokens.typography.fontWeights.regular,
    }),

    placeholder: (provided) => ({
        ...provided,
        color: tokens.colors.ui["muted-foreground"],
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: tokens.typography.fontWeights.regular,
    }),

    singleValue: (provided) => ({
        ...provided,
        color: tokens.colors.ui.foreground,
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: tokens.typography.fontWeights.regular,
    }),

    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused
            ? tokens.colors.brand.blue["50"]
            : "transparent",
        color: state.isFocused
            ? tokens.colors.brand.blue["500"]
            : tokens.colors.ui.foreground,
        fontFamily: tokens.typography.fontFamilies["albert-sans"],
        fontSize: tokens.typography.fontSize["lg-14px"],
        fontWeight: tokens.typography.fontWeights.regular,
        cursor: "pointer",
        padding: "8px 12px",
        "&:active": {
            backgroundColor: tokens.colors.brand.blue["100"],
        },
    }),

    menu: (provided) => ({
        ...provided,
        borderRadius: "6px",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        marginTop: "4px",
        border: `1px solid ${tokens.colors.ui.border}`,
        backgroundColor: tokens.colors.ui.background,
        zIndex: 9999,
    }),

    menuList: (provided) => ({
        ...provided,
        padding: "4px 0",
        maxHeight: "175px",
    }),

    dropdownIndicator: () => ({
        display: "none",
    }),

    indicatorSeparator: () => ({
        display: "none",
    }),

    clearIndicator: () => ({
        display: "none",
    }),
};

const SelectContainer = styled.div(({ size = "lg" }) => {
    const {
        padding,
        fontSize,
        lineHeight,
        borderRadius,
        height,
    } = sizes[size];

    return {
        display: "inline-flex",
        alignItems: "center",
        width: "100%",
        boxShadow: "0px 0px 0px 1px rgba(163, 163, 163, 0.50)",
        transition: "all 0.2s ease",
        gap: "4px",
        padding,
        fontSize,
        lineHeight,
        borderRadius,
        height,
        backgroundColor: tokens.colors.ui.background,
        border: `1px solid ${tokens.colors.ui.border}`,
        color: tokens.colors.ui.foreground,
        position: "relative", 

        "&:hover:not(.disabled)": {
            borderColor: tokens.colors.brand.blue["500"],
        },

        "&:focus-within:not(.disabled)": {
            ...defaultFocusVisibleState,
        },

        '&[data-error="true"]': {
            ...errorState,
        },

        "&:disabled, &.disabled": {
            opacity: 0.5,
            cursor: "not-allowed",
            backgroundColor: tokens.colors.brand.blue["100"],
        },

        "&:focus-within": {
            outline: "none",
        },

        "& .react-select__control": {
            flex: 1,
            width: "100%",
            minWidth: 0,
            height: "100%",
            backgroundColor: "transparent",
            border: "none",
            outline: "none",
            cursor: "pointer",

            "&:disabled": {
                cursor: "not-allowed",
            },
        },

        "& .dropdown-controls": {
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            pointerEvents: "none",
            height: "auto",
        },

        "& .divider": {
            width: "1px",
            height: "28px",
            backgroundColor: tokens.colors.ui.border,
            pointerEvents: "none",
        },

        "& .chevron": {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 2px",
            color: tokens.colors.ui.foreground,
            fontSize: "16px",
            lineHeight: "16px",
            pointerEvents: "none"
        },

        "& .clear-btn": {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            margin: 0,
            padding: "2px",
            color: tokens.colors.neutral.grey["500"],
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            pointerEvents: "auto",

            "&:hover": {
                color: tokens.colors.neutral.grey["700"],
            },

            "&:focus-visible": {
                outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
                borderRadius: "2px",
            },
        },
    };
});

export const FieldWrapper = styled.div(() => ({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
}));

export const Label = styled.label(() => ({
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.medium,
    lineHeight: "20px",
    color: tokens.colors.ui.foreground,
    display: "flex",
    alignItems: "center",
    gap: "4px",

    '&[data-required="true"]::after': {
        content: '"*"',
        color: tokens.colors.ui.destructive,
        marginLeft: "2px",
    },
}));

export const ErrorText = styled.span(() => ({
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.medium,
    lineHeight: "16px",
    color: tokens.colors.ui.destructive,
    minHeight: "1.4em",
}));

export const HelperText = styled.span(() => ({
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "16px",
    color: tokens.colors.ui["muted-foreground"],
}));

/**
 * A select dropdown component with label, error, helper text, and clearable support.
 *
 * @component
 * @param {Object} props
 * @param {Array} [props.options=[]] - Array of options (strings or {value, label} objects)
 * @param {string} [props.placeholder="Select item"] - Placeholder text
 * @param {string} [props.value] - Controlled value of the select
 * @param {function} [props.onChange] - Callback fired when value changes
 * @param {string} [props.size="lg"] - Size variant: "xs", "sm", "md", or "lg"
 * @param {boolean} [props.disabled=false] - If true, the select is disabled
 * @param {boolean} [props.error=false] - If true, applies error styling
 * @param {string} [props.label] - Label text
 * @param {string} [props.helperText] - Helper text to display
 * @param {string} [props.errorText] - Error message to display
 * @param {boolean} [props.required=false] - If true, shows required asterisk
 * @param {boolean} [props.isClearable=false] - If true, shows clear button
 * @param {function} [props.onClear] - Callback fired when clear button is clicked
 * @param {React.Ref} ref - Forwarded ref to the underlying select element
 * @returns {JSX.Element} The rendered select component
 */
const SelectDropdown = forwardRef(
    (
        {
            options = [],
            placeholder = "Select item",
            value,
            onChange,
            size = "lg",
            disabled = false,
            error = false,
            label,
            helperText,
            errorText,
            required = false,
            isClearable = false,
            onClear,
            ...props
        },
        ref
    ) => {
        const normalisedOptions = options.map((option) => {
            if (typeof option === "string") {
                return {
                    value: option,
                    label: option,
                };
            }

            if (option && typeof option === "object") {
                return {
                    value: option.value,
                    label: option.label || option.value,
                };
            }

            return {
                value: "",
                label: "",
            };
        });

        const showError = errorText ? true : error;
        const hasValue =
            value !== undefined && value !== null && value !== "";

        const selectedOption =
            normalisedOptions.find((option) => option.value === value) || null;

        const handleChange = (selected) => {
            if (onChange) {
                const event = {
                    target: {
                        value: selected ? selected.value : "",
                        name: props.name || "",
                    },
                };

                onChange(event);
            }
        };

        const handleClear = (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (onClear) {
                onClear();
            }

            if (onChange) {
                const event = {
                    target: {
                        value: "",
                        name: props.name || "",
                    },
                };

                onChange(event);
            }
        };

        return (
            <FieldWrapper>
                {label && (
                    <Label
                        htmlFor={props.id}
                        data-required={required ? "true" : undefined}
                    >
                        {label}
                    </Label>
                )}

                <SelectContainer
                    size={size}
                    className={disabled ? "disabled" : ""}
                    data-error={showError ? "true" : undefined}
                    $hasValue={hasValue}
                >
                    <Select
                        ref={ref}
                        className="react-select__control"
                        classNamePrefix="react-select"
                        options={normalisedOptions}
                        value={selectedOption}
                        onChange={handleChange}
                        placeholder={placeholder}
                        isDisabled={disabled}
                        isClearable={false}
                        isSearchable={false}
                        styles={customSelectStyles}
                        maxMenuHeight={175}
                        aria-invalid={showError || undefined}
                        {...props}
                    />

                    <div className="dropdown-controls">
                        {isClearable && hasValue && !disabled && (
                            <button
                                type="button"
                                className="clear-btn"
                                aria-label={`Clear ${label || "selection"}`}
                                onClick={handleClear}
                            >
                                <i
                                    className="fal fa-times"
                                    aria-hidden="true"
                                />
                            </button>
                        )}

                        <span
                            className="divider"
                            aria-hidden="true"
                        />

                        <span
                            className="chevron"
                            aria-hidden="true"
                        >
                            <i className="fal fa-chevron-down" />
                        </span>
                    </div>
                </SelectContainer>

                {errorText ? (
                    <ErrorText>{errorText}</ErrorText>
                ) : helperText ? (
                    <HelperText>{helperText}</HelperText>
                ) : null}
            </FieldWrapper>
        );
    }
);

SelectDropdown.displayName = "SelectDropdown";

export default SelectDropdown;